import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import {
  Settings as SettingsIcon, DollarSign, Users, Plus,
  Loader2, CheckCircle2, X, AlertTriangle, RefreshCw, Bell, Shield,
  Download, Database, Archive, Copy, Check, Link as LinkIcon, ExternalLink,
  Brain, Trash2, ChevronDown, ChevronRight, UploadCloud, RotateCcw
} from "lucide-react";
import { formatPYGCompact, formatDate } from "../utils/format";
import axios from "axios";
import { API } from "../contexts/AuthContext";
import { useAuth } from "../contexts/AuthContext";
import { Skeleton } from "../components/ui/skeleton";
import { toast } from "sonner";
import { esRestauracionCompleta } from "../lib/restoreResultado";
import { ROLE_VALUES } from "../constants/roles";

function NewUserModal({ open, onClose, onSaved }) {
  const [form, setForm] = useState({ email: "", name: "", role: "", password: "" });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState("");

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.email || !form.name || !form.password) { setError("Complete todos los campos"); return; }
    if (!form.role) { setError("Seleccione un rol"); return; }
    setSaving(true); setError("");
    try {
      await axios.post(`${API}/settings/users`, form, { withCredentials: true });
      toast.success(`Usuario ${form.email} creado`);
      onSaved(); onClose();
    } catch (err) {
      setError(err.response?.data?.detail || "Error al crear usuario");
    } finally { setSaving(false); }
  };

  if (!open) return null;
  return createPortal(
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md" data-testid="new-user-modal">
        <div className="flex items-center justify-between px-6 py-5 border-b border-gray-100">
          <h2 className="text-lg font-bold text-gray-900">Nuevo Usuario</h2>
          <button onClick={onClose} className="p-2 rounded-lg hover:bg-gray-100"><X size={18} className="text-gray-500" /></button>
        </div>
        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Nombre Completo *</label>
            <input name="name" value={form.name} onChange={handleChange} placeholder="Nombre del usuario" className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4]" data-testid="user-name-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Email *</label>
            <input name="email" type="email" value={form.email} onChange={handleChange} placeholder="usuario@cen.com.py" className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4]" data-testid="user-email-input" />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Rol *</label>
            <select name="role" value={form.role} onChange={handleChange} className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4] bg-white" data-testid="user-role-select">
              <option value="" disabled>Seleccionar rol...</option>
              {ROLE_VALUES.map(r => <option key={r} value={r}>{r}</option>)}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1.5">Contraseña Inicial *</label>
            <input name="password" type="password" value={form.password} onChange={handleChange} placeholder="••••••••" className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4]" data-testid="user-password-input" />
          </div>
          {error && <div className="flex items-center gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2 text-sm"><AlertTriangle size={14} />{error}</div>}
          <div className="flex gap-3 pt-2">
            <button type="button" onClick={onClose} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium">Cancelar</button>
            <button type="submit" disabled={saving} className="flex-1 bg-[#2E6DA4] hover:bg-[#245A8A] text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-60" data-testid="save-user-btn">
              {saving ? <Loader2 size={15} className="animate-spin" /> : <CheckCircle2 size={15} />}
              {saving ? "Guardando..." : "Crear Usuario"}
            </button>
          </div>
        </form>
      </div>
    </div>
  , document.body);
}

// BLOQUE 6 PARTE C.1 — Reseteo de datos TEST y DEMO (solo Superadministrador)
function TestDataCleanupCard() {
  const [report, setReport] = useState(null);
  const [loadingReport, setLoadingReport] = useState(false);
  const [expanded, setExpanded] = useState({});
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [executing, setExecuting] = useState(false);
  const [execResult, setExecResult] = useState(null);

  const fetchReport = async () => {
    setLoadingReport(true);
    setExecResult(null);
    try {
      const { data } = await axios.post(`${API}/admin/test-data-cleanup?confirm=false`, {}, { withCredentials: true });
      setReport(data);
      toast.success(`Reporte generado: ${data.total_documents} documentos candidatos a eliminar`);
    } catch (err) { toast.error(err.response?.data?.detail || "Error al generar el reporte"); }
    finally { setLoadingReport(false); }
  };

  const handleExecute = async () => {
    if (confirmText !== "ELIMINAR") return;
    setExecuting(true);
    try {
      const { data } = await axios.post(`${API}/admin/test-data-cleanup?confirm=true`, {}, { withCredentials: true });
      setExecResult(data);
      setShowConfirm(false);
      setConfirmText("");
      toast.success(data.message);
      fetchReport();
    } catch (err) { toast.error(err.response?.data?.detail || "Error al ejecutar la limpieza"); }
    finally { setExecuting(false); }
  };

  const toggleExpand = (col) => setExpanded(prev => ({ ...prev, [col]: !prev[col] }));
  const nonEmptyCols = report ? Object.entries(report.report).filter(([, info]) => info.count > 0) : [];

  return (
    <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm overflow-hidden" data-testid="test-data-cleanup-card">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
        <div className="p-2 bg-red-50 rounded-xl"><Trash2 size={16} className="text-red-600" /></div>
        <div>
          <h3 className="font-semibold text-gray-900">Reseteo de Datos TEST y DEMO</h3>
          <p className="text-xs text-gray-500">Detecta y limpia proyectos, presupuestos y documentos de prueba/demo que inflan el dashboard</p>
        </div>
      </div>
      <div className="p-5 space-y-4">
        <div className="bg-blue-50 border border-blue-200 rounded-xl p-4 text-sm text-blue-800">
          <p className="font-semibold mb-1">¿Cómo funciona?</p>
          <p className="text-xs text-blue-700">1) Generá el reporte (dry-run) — no borra nada. 2) Revisá qué se eliminaría por colección. 3) Si todo está correcto, ejecutá la limpieza real (genera backup automático antes de borrar). Los presupuestos protegidos (Costa Sole, Oficinas BBC, Mansión Anita, BeLive2) y usuarios/configuración nunca se tocan.</p>
        </div>

        <button onClick={fetchReport} disabled={loadingReport} className="flex items-center gap-2 border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-60" data-testid="generate-cleanup-report-btn">
          {loadingReport ? <Loader2 size={15} className="animate-spin" /> : <RefreshCw size={15} />}
          {loadingReport ? "Generando reporte..." : "Generar Reporte (Dry-Run)"}
        </button>

        {report && (
          <div className="space-y-3" data-testid="cleanup-report-results">
            <div className="flex items-center gap-2">
              <span className="text-2xl font-bold text-gray-900">{report.total_documents}</span>
              <span className="text-sm text-gray-500">documentos candidatos en {nonEmptyCols.length} colecciones</span>
            </div>
            <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 max-h-96 overflow-y-auto">
              {nonEmptyCols.map(([col, info]) => (
                <div key={col}>
                  <button onClick={() => toggleExpand(col)} className="w-full flex items-center justify-between px-4 py-2.5 hover:bg-gray-50 text-left" data-testid={`cleanup-col-${col}`}>
                    <span className="text-sm font-medium text-gray-800">{col}</span>
                    <div className="flex items-center gap-2">
                      <span className="text-xs bg-red-100 text-red-700 font-bold px-2 py-0.5 rounded-full">{info.count}</span>
                      {expanded[col] ? <ChevronDown size={14} className="text-gray-400" /> : <ChevronRight size={14} className="text-gray-400" />}
                    </div>
                  </button>
                  {expanded[col] && (
                    <div className="px-4 pb-3 space-y-1 max-h-48 overflow-y-auto">
                      {info.items.slice(0, 100).map((it, i) => (
                        <p key={i} className="text-xs text-gray-500">
                          <span className="font-mono">{it.identifier || it.id.slice(-6)}</span> — {it.name || "(sin nombre)"}
                        </p>
                      ))}
                      {info.items.length > 100 && <p className="text-xs text-gray-400">... y {info.items.length - 100} más</p>}
                    </div>
                  )}
                </div>
              ))}
            </div>

            {report.total_documents > 0 && (
              <button onClick={() => setShowConfirm(true)} className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 rounded-xl text-sm font-semibold" data-testid="execute-cleanup-btn">
                <Trash2 size={15} /> Ejecutar Limpieza Real
              </button>
            )}
          </div>
        )}

        {execResult && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-800" data-testid="cleanup-exec-result">
            <p className="font-semibold">{execResult.message}</p>
            <p className="text-xs mt-1">Total eliminado: {execResult.total_deleted} · Backup: <span className="font-mono">{execResult.backup_file}</span></p>
          </div>
        )}
      </div>

      {showConfirm && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !executing && setShowConfirm(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4" data-testid="confirm-cleanup-modal">
            <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
              <AlertTriangle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">
                Estás a punto de eliminar <span className="font-bold">{report?.total_documents}</span> documentos de forma permanente (se generará un backup automático antes). Escribí <span className="font-mono font-bold">ELIMINAR</span> para confirmar.
              </p>
            </div>
            <input value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder="ELIMINAR" className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm" data-testid="confirm-cleanup-input" />
            <div className="flex gap-3">
              <button onClick={() => { setShowConfirm(false); setConfirmText(""); }} disabled={executing} className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium disabled:opacity-60">Cancelar</button>
              <button onClick={handleExecute} disabled={confirmText !== "ELIMINAR" || executing} className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-40" data-testid="confirm-cleanup-execute-btn">
                {executing ? <Loader2 size={15} className="animate-spin" /> : <Trash2 size={15} />}
                {executing ? "Ejecutando..." : "Confirmar y Eliminar"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

// Auditoría 2026-08-27, CAMBIO 4 — Datos de empresa centralizados (razón
// social, RUC, dirección, teléfono, email, logo). Los leen los 4 PDFs
// (Presupuesto, Certificado, Orden de Pago, Cobranza). RUC/dirección/teléfono
// arrancan vacíos: el cliente los completa acá.
function EmpresaSettingsCard() {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings/empresa`, { withCredentials: true })
      .then(({ data }) => setForm(data))
      .catch(() => toast.error("Error al cargar los datos de la empresa"))
      .finally(() => setLoading(false));
  }, []);

  const handleChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/settings/empresa`, form, { withCredentials: true });
      setForm(data);
      toast.success("Datos de la empresa actualizados");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar los datos de la empresa");
    } finally { setSaving(false); }
  };

  return (
    <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm overflow-hidden" data-testid="empresa-settings-card">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
        <div className="p-2 bg-blue-50 rounded-xl"><SettingsIcon size={16} className="text-blue-600" /></div>
        <div>
          <h3 className="font-semibold text-gray-900">Datos de la Empresa</h3>
          <p className="text-xs text-gray-500">Encabezado de los PDFs de Presupuesto, Certificado, Orden de Pago y Cobranza</p>
        </div>
      </div>
      {loading || !form ? (
        <div className="p-5"><Loader2 className="animate-spin text-gray-400" size={20} /></div>
      ) : (
        <div className="p-5 space-y-3">
          {[
            { key: "razon_social", label: "Razón Social" },
            { key: "descripcion", label: "Descripción / Rubro" },
            { key: "ruc", label: "RUC" },
            { key: "direccion", label: "Dirección" },
            { key: "telefono", label: "Teléfono" },
            { key: "email", label: "Email" },
            { key: "logo_url", label: "URL del Logo (opcional)" },
          ].map((f) => (
            <div key={f.key}>
              <label className="text-xs font-medium text-gray-600 mb-1 block">{f.label}</label>
              <input
                name={f.key}
                value={form[f.key] || ""}
                onChange={handleChange}
                placeholder={f.key === "ruc" || f.key === "direccion" || f.key === "telefono" ? "Sin completar" : ""}
                data-testid={`empresa-input-${f.key}`}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
              />
            </div>
          ))}
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="empresa-settings-save-btn"
            className="mt-2 px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
          >
            {saving ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle2 size={14} />}
            Guardar
          </button>
        </div>
      )}
    </div>
  );
}

// Fix DI (2026-09-17): textos por defecto de la pestaña Alcance / Observaciones
// del presupuesto. Se cargan una sola vez acá y de ahí en más todo presupuesto
// nuevo nace con ellos. El botón "Traer de un presupuesto" evita tener que
// copiarlos a mano: se elige uno ya cargado y trae sus cuatro textos, que quedan
// a la vista para revisar antes de guardar.
function TextosPresupuestoCard() {
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [presupuestos, setPresupuestos] = useState([]);
  const [origenId, setOrigenId] = useState("");
  const [trayendo, setTrayendo] = useState(false);

  useEffect(() => {
    axios.get(`${API}/settings/textos-presupuesto`, { withCredentials: true })
      .then(({ data }) => setForm(data))
      .catch(() => toast.error("Error al cargar los textos por defecto"))
      .finally(() => setLoading(false));
    axios.get(`${API}/budgets`, { withCredentials: true })
      .then(({ data }) => setPresupuestos(Array.isArray(data) ? data : (data?.items || [])))
      .catch(() => setPresupuestos([]));
  }, []);

  const traerDe = async () => {
    if (!origenId) return;
    setTrayendo(true);
    try {
      const { data } = await axios.get(`${API}/budgets/${origenId}`, { withCredentials: true });
      setForm(f => ({
        ...f,
        alcance_incluye: data.alcance_incluye || "",
        alcance_no_incluye: data.alcance_no_incluye || "",
        obs_tecnicas: data.obs_tecnicas || "",
        condicion_pago: data.condicion_pago || "",
      }));
      toast.success("Textos traídos. Revisalos y guardá.");
    } catch {
      toast.error("No se pudieron traer los textos de ese presupuesto");
    } finally { setTrayendo(false); }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data } = await axios.put(`${API}/settings/textos-presupuesto`, {
        alcance_incluye: form.alcance_incluye || "",
        alcance_no_incluye: form.alcance_no_incluye || "",
        obs_tecnicas: form.obs_tecnicas || "",
        condicion_pago: form.condicion_pago || "",
      }, { withCredentials: true });
      setForm(data);
      toast.success("Textos por defecto actualizados");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al guardar los textos por defecto");
    } finally { setSaving(false); }
  };

  const CAMPOS = [
    { key: "alcance_incluye", label: "EL PRESUPUESTO SÍ INCLUYE", rows: 6 },
    { key: "alcance_no_incluye", label: "EL PRESUPUESTO NO INCLUYE", rows: 6 },
    { key: "obs_tecnicas", label: "OBSERVACIONES TÉCNICAS", rows: 5 },
    { key: "condicion_pago", label: "CONDICIÓN DE PAGO", rows: 2 },
  ];

  return (
    <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm overflow-hidden" data-testid="textos-presupuesto-card">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
        <div className="p-2 bg-emerald-50 rounded-xl"><SettingsIcon size={16} className="text-emerald-600" /></div>
        <div>
          <h3 className="font-semibold text-gray-900">Textos por Defecto del Presupuesto</h3>
          <p className="text-xs text-gray-500">Se cargan solos al crear un presupuesto nuevo, en la pestaña Alcance / Observaciones. Se pueden editar o borrar en cada presupuesto.</p>
        </div>
      </div>
      {loading || !form ? (
        <div className="p-5"><Loader2 className="animate-spin text-gray-400" size={20} /></div>
      ) : (
        <div className="p-5 space-y-4">
          <div className="flex flex-wrap items-end gap-2 bg-gray-50 border border-gray-200 rounded-xl p-3">
            <div className="flex-1 min-w-[220px]">
              <label className="text-xs font-medium text-gray-600 mb-1 block">Traer los textos de un presupuesto ya cargado</label>
              <select value={origenId} onChange={e => setOrigenId(e.target.value)}
                data-testid="textos-origen-select"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white">
                <option value="">— Elegir presupuesto —</option>
                {presupuestos.map(b => (
                  <option key={b._id || b.id} value={b._id || b.id}>{`${b.number || ""} · ${b.nombre || b.obra || ""}`}</option>
                ))}
              </select>
            </div>
            <button onClick={traerDe} disabled={!origenId || trayendo}
              data-testid="textos-traer-btn"
              className="px-4 py-2 border border-gray-300 text-gray-700 text-sm font-medium rounded-lg hover:bg-gray-100 disabled:opacity-50 flex items-center gap-2">
              {trayendo ? <Loader2 className="animate-spin" size={14} /> : null}
              Traer
            </button>
          </div>
          {CAMPOS.map(f => (
            <div key={f.key}>
              <label className="text-xs font-bold text-[#1A4B7A] mb-1 block uppercase tracking-wide">{f.label}</label>
              <textarea
                value={form[f.key] || ""}
                onChange={e => setForm({ ...form, [f.key]: e.target.value })}
                rows={f.rows}
                placeholder="Sin completar"
                data-testid={`textos-input-${f.key}`}
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-emerald-500 resize-y"
              />
            </div>
          ))}
          <button onClick={handleSave} disabled={saving}
            data-testid="textos-presupuesto-save-btn"
            className="mt-1 px-4 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 disabled:opacity-50 flex items-center gap-2">
            {saving ? <Loader2 className="animate-spin" size={14} /> : <CheckCircle2 size={14} />}
            Guardar
          </button>
          {form.updated_at && (
            <p className="text-[11px] text-gray-400">Última actualización: {new Date(form.updated_at).toLocaleString("es-PY")}{form.updated_by ? ` · ${form.updated_by}` : ""}</p>
          )}
        </div>
      )}
    </div>
  );
}

// Fix DN (2026-09-21) / Fix EX (2026-10-05): quién aprueba pedidos y análisis se configura
// ahora en Compras → Rango p/ Aprobaciones (perfiles y rangos de monto).
function AprobadoresPedidosCard() {
  return (
    <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm overflow-hidden" data-testid="aprobadores-pedidos-card">
      <div className="flex items-center gap-3 px-5 py-4">
        <div className="p-2 bg-emerald-50 rounded-xl"><CheckCircle2 size={16} className="text-emerald-600" /></div>
        <div>
          <h3 className="font-semibold text-gray-900">Aprobación de Pedidos</h3>
          <p className="text-xs text-gray-500">Ahora se configura en Compras → <a href="/compras/materiales?tab=rangos" className="text-[#2E6DA4] font-semibold underline">Rango p/ Aprobaciones</a>, junto con los rangos de monto del Análisis de Presupuesto.</p>
        </div>
      </div>
    </div>
  );
}

// BLOQUE 29 — Restaurar backup (solo Superadministrador). Sube el ZIP v1.1
// generado por /api/admin/full-backup, permite Verificar (dry-run, no toca
// datos) y Restaurar (destructivo, exige escribir "RESTAURAR" para confirmar).
function RestoreBackupCard() {
  const [file, setFile] = useState(null);
  const [verifying, setVerifying] = useState(false);
  const [verifyResult, setVerifyResult] = useState(null);
  const [verifyError, setVerifyError] = useState("");
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmText, setConfirmText] = useState("");
  const [restoring, setRestoring] = useState(false);
  const [restoreResult, setRestoreResult] = useState(null);
  const [restoreError, setRestoreError] = useState("");
  const [restoreFailure, setRestoreFailure] = useState(null);
  const [diag, setDiag] = useState(null);
  const [diagLoading, setDiagLoading] = useState(false);
  const [diagError, setDiagError] = useState("");
  // Fix BI (2026-09-08): partes subidas de un backup grande.
  const [progreso, setProgreso] = useState(null);

  const handleFileChange = (e) => {
    setFile(e.target.files?.[0] || null);
    setVerifyResult(null); setVerifyError("");
    setRestoreResult(null); setRestoreError(""); setRestoreFailure(null);
    setProgreso(null);
  };

  // Fix BI (2026-09-08): el servidor corta los envíos de más de 100 MB y el
  // backup completo pesa cerca de 190 MB. Los archivos grandes se mandan en
  // partes de 40 MB y se arman del otro lado. Los chicos van de una sola vez,
  // como siempre.
  const TAMANO_PARTE = 40 * 1024 * 1024;

  const subirEnPartes = async (archivo) => {
    const total = Math.ceil(archivo.size / TAMANO_PARTE);
    let uploadId = "";
    for (let i = 0; i < total; i++) {
      const trozo = archivo.slice(i * TAMANO_PARTE, (i + 1) * TAMANO_PARTE);
      const fd = new FormData();
      fd.append("file", trozo, `parte_${i}`);
      fd.append("indice", String(i));
      fd.append("total", String(total));
      if (uploadId) fd.append("upload_id", uploadId);
      const { data } = await axios.post(`${API}/admin/full-restore/parte`, fd, { withCredentials: true });
      uploadId = data.upload_id;
      setProgreso({ hechas: i + 1, total });
    }
    setProgreso(null);
    return uploadId;
  };

  const handleVerify = async () => {
    if (!file) { toast.error("Seleccioná primero un archivo .zip"); return; }
    setVerifying(true); setVerifyError(""); setVerifyResult(null); setRestoreResult(null);
    try {
      const fd = new FormData();
      fd.append("dry_run", "true");
      if (file.size > TAMANO_PARTE) {
        fd.append("upload_id", await subirEnPartes(file));
      } else {
        fd.append("file", file);
      }
      const { data } = await axios.post(`${API}/admin/full-restore`, fd, { withCredentials: true });
      setVerifyResult(data);
      toast.success("Backup verificado: es un ZIP válido para restaurar");
    } catch (err) {
      const msg = err.response?.data?.detail || "El archivo no es un backup válido para restaurar";
      setVerifyError(msg);
      toast.error(msg);
    } finally { setVerifying(false); }
  };

  const handleRestore = async () => {
    if (confirmText !== "RESTAURAR" || !file) return;
    setRestoring(true); setRestoreError(""); setRestoreFailure(null); setRestoreResult(null);
    try {
      const fd = new FormData();
      if (file.size > TAMANO_PARTE) {
        fd.append("upload_id", await subirEnPartes(file));
      } else {
        fd.append("file", file);
      }
      const { data } = await axios.post(`${API}/admin/full-restore?wipe_first=true`, fd, { withCredentials: true });
      setShowConfirm(false);
      setConfirmText("");
      if (esRestauracionCompleta(data)) {
        setRestoreResult(data);
        toast.success("Backup restaurado correctamente");
      } else {
        setRestoreFailure(data);
        toast.error("La restauración NO se completó");
      }
    } catch (err) {
      const d = err.response?.data;
      if (d && typeof d === "object" && !esRestauracionCompleta(d) && (d.status === "failed" || d.status === "incomplete")) {
        setRestoreFailure(d);
        setShowConfirm(false);
        setConfirmText("");
        toast.error(d.status === "failed" ? "Restauración FALLIDA" : "Restauración INCOMPLETA");
      } else {
        const msg = (typeof d?.detail === "string" && d.detail) || "Error al restaurar el backup";
        setRestoreError(msg);
        toast.error(msg);
      }
      setProgreso(null);
    } finally { setRestoring(false); }
  };

  const handleDiagnostico = async () => {
    setDiagLoading(true); setDiagError(""); setDiag(null);
    try {
      const { data } = await axios.get(`${API}/admin/full-restore/diagnostico`, { withCredentials: true });
      setDiag(data);
    } catch (err) {
      setDiagError((typeof err.response?.data?.detail === "string" && err.response.data.detail) || "No se pudo consultar el destino");
    } finally { setDiagLoading(false); }
  };

  const nd = (v) => (v === null || v === undefined ? "no disponible" : v.toLocaleString("es-PY"));
  const errTxt = (e) => (e ? ` (error ${e.code ?? "s/c"} ${e.name})` : "");

  // Fix BI (2026-09-08): aviso de avance mientras se sube un backup grande.
  const avisoProgreso = progreso ? (
    <p className="text-xs text-[#2E6DA4] font-semibold" data-testid="restore-progreso">
      Subiendo el backup: parte {progreso.hechas} de {progreso.total}
    </p>
  ) : null;

  const collectionEntries = verifyResult?.would_restore?.collections
    ? Object.entries(verifyResult.would_restore.collections)
    : [];

  return (
    <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm overflow-hidden" data-testid="restore-backup-card">
      <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
        <div className="p-2 bg-orange-50 rounded-xl"><Database size={16} className="text-orange-600" /></div>
        <div>
          <h3 className="font-semibold text-gray-900">Restaurar Backup</h3>
          <p className="text-xs text-gray-500">Sube un ZIP generado por "Backup Completo del Sistema" (arriba) para verificarlo o restaurarlo</p>
        </div>
      </div>
      <div className="p-5 space-y-4">
        <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-800">
          <p className="font-semibold mb-1">Acción destructiva</p>
          <p className="text-xs text-red-700">Restaurar reemplaza las colecciones incluidas en el ZIP por las del backup. Verificá primero (dry-run, no borra nada) y revisá el reporte antes de restaurar. Después de restaurar, reiniciá el backend para asegurar consistencia total.</p>
        </div>

        <div>
          <label className="block text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Archivo de backup (.zip)</label>
          <input
            type="file"
            accept=".zip"
            onChange={handleFileChange}
            className="w-full text-sm border border-gray-300 rounded-xl px-3 py-2.5 file:mr-3 file:py-1.5 file:px-3 file:rounded-lg file:border-0 file:bg-[#2E6DA4] file:text-white file:text-xs file:font-semibold"
            data-testid="restore-file-input"
          />
        </div>

        <button
          onClick={handleVerify}
          disabled={!file || verifying}
          className="flex items-center gap-2 border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-60"
          data-testid="verify-restore-btn"
        >
          {verifying ? <Loader2 size={15} className="animate-spin" /> : <UploadCloud size={15} />}
          {verifying ? "Verificando..." : "Verificar (Dry-Run)"}
        </button>

        {avisoProgreso}

        <button
          onClick={handleDiagnostico}
          disabled={diagLoading}
          className="flex items-center gap-2 border border-gray-300 text-gray-700 hover:bg-gray-50 px-4 py-2.5 rounded-xl text-sm font-semibold disabled:opacity-60"
          data-testid="diagnostico-destino-btn"
        >
          {diagLoading ? <Loader2 size={15} className="animate-spin" /> : <RotateCcw size={15} />}
          {diagLoading ? "Comprobando..." : "Comprobar destino"}
        </button>

        {diagError && (
          <div className="bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2.5 text-sm" data-testid="diagnostico-error">{diagError}</div>
        )}

        {diag && (
          <div className="border border-gray-200 rounded-xl p-4 text-xs text-gray-700 space-y-1.5" data-testid="diagnostico-resultado">
            <p className="font-semibold text-gray-900">Diagnóstico del destino (solo lectura) — base: {diag.database?.name || "no disponible"}</p>
            {diag.database?.available ? (
              <>
                <p>Datos: {nd(diag.database.data_size)} B · Almacenamiento: {nd(diag.database.storage_size)} B · Índices: {nd(diag.database.index_size)} B · Total: {nd(diag.database.total_size)} B</p>
                <p>Filesystem/clúster (no es una cuota de esta base): usado {nd(diag.database.fs_used_size)} B de {nd(diag.database.fs_total_size)} B</p>
              </>
            ) : (
              <p data-testid="diagnostico-db-nd">Estadísticas de la base: no disponible{errTxt(diag.database?.error)}</p>
            )}
            {diag.restore_partes?.available ? (
              <p>restore_partes: {diag.restore_partes.files_count} archivos ({nd(diag.restore_partes.files_bytes)} B) · {diag.restore_partes.chunks_count} chunks</p>
            ) : (
              <p>restore_partes: no disponible{errTxt(diag.restore_partes?.error)}</p>
            )}
            {diag.write_block?.available ? (
              <p className={diag.write_block.blocked ? "font-semibold text-red-700" : ""} data-testid="diagnostico-bloqueo">
                Bloqueo de escrituras: modo {diag.write_block.mode} ({diag.write_block.mode_label || "desconocido"})
                {diag.write_block.reason !== null && diag.write_block.reason !== undefined ? ` · motivo ${diag.write_block.reason} (${diag.write_block.reason_label || "desconocido"})` : ""}
              </p>
            ) : (
              <p className="font-semibold text-amber-700" data-testid="diagnostico-bloqueo">
                Bloqueo de escrituras: no disponible{errTxt(diag.write_block?.error)} — no significa que esté desbloqueado
              </p>
            )}
          </div>
        )}

        {verifyError && (
          <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2.5 text-sm" data-testid="verify-restore-error">
            <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
            <span>{verifyError}</span>
          </div>
        )}

        {verifyResult && (
          <div className="space-y-3" data-testid="verify-restore-result">
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-800">
              <p className="font-semibold mb-1">ZIP válido — listo para restaurar</p>
              <p className="text-xs text-emerald-700">
                Versión: <span className="font-mono">{verifyResult.manifest?.backup_version}</span> ·
                {" "}Generado: {formatDate(verifyResult.manifest?.created_at)} ·
                {" "}Por: {verifyResult.manifest?.created_by?.email || "desconocido"}
              </p>
            </div>
            <div className="border border-gray-200 rounded-xl divide-y divide-gray-100 max-h-64 overflow-y-auto">
              {collectionEntries.map(([col, count]) => (
                <div key={col} className="flex items-center justify-between px-4 py-2 text-sm">
                  <span className="text-gray-700">{col}</span>
                  <span className="text-xs bg-blue-50 text-blue-700 font-bold px-2 py-0.5 rounded-full">{count ?? 0} docs</span>
                </div>
              ))}
            </div>
            <p className="text-xs text-gray-500">
              GridFS: {verifyResult.would_restore?.gridfs_files ?? 0} archivos · Uploads: {verifyResult.would_restore?.uploads_files ?? 0} archivos
            </p>
            {verifyResult.warnings?.length > 0 && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-3 text-xs text-amber-700">
                {verifyResult.warnings.map((w, i) => <p key={i}>⚠ {w}</p>)}
              </div>
            )}
            <button
              onClick={() => setShowConfirm(true)}
              className="flex items-center gap-2 bg-red-600 hover:bg-red-700 text-white px-4 py-2.5 rounded-xl text-sm font-semibold"
              data-testid="open-restore-confirm-btn"
            >
              <RotateCcw size={15} /> Restaurar Ahora
            </button>
          </div>
        )}

        {restoreError && (
          <div className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 rounded-xl px-3 py-2.5 text-sm" data-testid="restore-error">
            <AlertTriangle size={14} className="flex-shrink-0 mt-0.5" />
            <span>{restoreError}</span>
          </div>
        )}

        {restoreFailure && (
          <div className="bg-red-50 border border-red-300 rounded-xl p-4 text-sm text-red-800 space-y-1.5" data-testid="restore-failure">
            <p className="font-semibold" data-testid="restore-failure-title">
              {restoreFailure.status === "failed" ? "Restauración FALLIDA" : "Restauración INCOMPLETA"} — no usar el destino como copia fiel
            </p>
            <p className="text-xs" data-testid="restore-failure-detail">{restoreFailure.detail}</p>
            <p className="text-xs">
              Restaurado hasta ahora: {restoreFailure.docs_restored ?? 0} documentos · {restoreFailure.gridfs_files_restored ?? 0} archivos GridFS · {restoreFailure.files_restored ?? 0} adjuntos
            </p>
            {restoreFailure.pending?.collections?.length > 0 && (
              <p className="text-xs" data-testid="restore-failure-pending">
                Colecciones pendientes/con error ({restoreFailure.pending.collections.length}): {restoreFailure.pending.collections.slice(0, 15).join(", ")}{restoreFailure.pending.collections.length > 15 ? "…" : ""}
              </p>
            )}
            {restoreFailure.pending?.gridfs_files > 0 && (
              <p className="text-xs">GridFS pendiente: {restoreFailure.pending.gridfs_files} archivos</p>
            )}
            {restoreFailure.errors?.length > 0 && (
              <p className="text-xs">{restoreFailure.errors.length} error(es) registrados</p>
            )}
          </div>
        )}

        {restoreResult && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm text-emerald-800 space-y-1.5" data-testid="restore-result">
            <p className="font-semibold">Restauración completada ({restoreResult.duration_ms} ms)</p>
            <p className="text-xs text-emerald-700">
              {restoreResult.docs_restored} documentos · {restoreResult.gridfs_files_restored} archivos GridFS · {restoreResult.files_restored} adjuntos
            </p>
            <p className="text-xs text-emerald-700">
              Smoke test: {restoreResult.smoke_test?.users_count} usuarios, {restoreResult.smoke_test?.budgets_count} presupuestos, admin presente: {restoreResult.smoke_test?.admin_user_present ? "sí" : "no"}
            </p>
            {restoreResult.files_missing?.length > 0 && (
              <p className="text-xs text-amber-700">⚠ {restoreResult.files_missing.length} adjuntos referenciados sin archivo en el ZIP</p>
            )}
            {restoreResult.errors?.length > 0 && (
              <p className="text-xs text-red-700">⚠ {restoreResult.errors.length} errores durante la restauración (ver detalle en el servidor)</p>
            )}
            <p className="text-xs font-semibold text-emerald-900 pt-1">Reiniciá el backend ahora para asegurar consistencia total del sistema.</p>
          </div>
        )}
      </div>

      {showConfirm && createPortal(
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => !restoring && setShowConfirm(false)} />
          <div className="relative bg-white rounded-2xl shadow-2xl w-full max-w-md p-6 space-y-4" data-testid="confirm-restore-modal">
            <div className="flex items-start gap-3 bg-red-50 border border-red-200 rounded-xl p-4">
              <AlertTriangle size={18} className="text-red-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-red-800">
                Estás a punto de <span className="font-bold">reemplazar</span> los datos actuales por los del backup. Esta acción no se puede deshacer. Escribí <span className="font-mono font-bold">RESTAURAR</span> para confirmar.
              </p>
            </div>
            <input
              value={confirmText}
              onChange={(e) => setConfirmText(e.target.value)}
              placeholder="RESTAURAR"
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm"
              data-testid="confirm-restore-input"
            />
            <div className="flex gap-3">
              <button
                onClick={() => { setShowConfirm(false); setConfirmText(""); }}
                disabled={restoring}
                className="flex-1 border border-gray-300 text-gray-700 hover:bg-gray-50 rounded-xl py-2.5 text-sm font-medium disabled:opacity-60"
              >
                Cancelar
              </button>
              <button
                onClick={handleRestore}
                disabled={confirmText !== "RESTAURAR" || restoring}
                className="flex-1 bg-red-600 hover:bg-red-700 text-white rounded-xl py-2.5 text-sm font-semibold flex items-center justify-center gap-2 disabled:opacity-40"
                data-testid="confirm-restore-execute-btn"
              >
                {restoring ? <Loader2 size={15} className="animate-spin" /> : <RotateCcw size={15} />}
                {restoring ? "Restaurando..." : "Confirmar y Restaurar"}
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
}

export default function Settings() {
  const { user } = useAuth();
  const isAdmin = user?.role === "Administrador" || user?.role === "Superadministrador";
  const [exchangeRate, setExchangeRate] = useState({ usd_pyg: 7500 });
  const [historialTC, setHistorialTC] = useState([]);   // Fix FG (2026-10-08)
  const [rateInput, setRateInput] = useState("7500");
  const [savingRate, setSavingRate] = useState(false);
  const [users, setUsers] = useState([]);
  const [loadingUsers, setLoadingUsers] = useState(true);
  const [showNewUser, setShowNewUser] = useState(false);
  const [liquidityThreshold, setLiquidityThreshold] = useState(0);
  const [liquidityInput, setLiquidityInput] = useState("0");
  const [savingLiquidity, setSavingLiquidity] = useState(false);
  const [downloadingBackup, setDownloadingBackup] = useState(false);
  const [linkCopied, setLinkCopied] = useState(false);
  // C.3 — Config IA auto-aplicar
  const [aiSettings, setAiSettings] = useState({ enabled: true, confidence_threshold: 95, max_total_diff_pct: 5.0 });
  const [aiInput, setAiInput] = useState({ enabled: true, confidence_threshold: "95", max_total_diff_pct: "5" });
  const [savingAI, setSavingAI] = useState(false);
  const isSuperAdmin = user?.role === "Superadministrador";
  const backupUrl = `${API}/admin/full-backup`;

  useEffect(() => {
    // Fix FG (2026-10-08): el tipo de cambio del día (automático, con corrección manual).
    axios.get(`${API}/tipo-cambio`, { withCredentials: true })
      .then(({ data }) => { setExchangeRate(data); setRateInput(String(data.usd_pyg)); })
      .catch(() => {});
    axios.get(`${API}/tipo-cambio/historial?dias=15`, { withCredentials: true })
      .then(({ data }) => setHistorialTC(Array.isArray(data) ? data : []))
      .catch(() => {});
    axios.get(`${API}/settings/liquidity-threshold`, { withCredentials: true })
      .then(({ data }) => { setLiquidityThreshold(data.threshold_pyg || 0); setLiquidityInput(String(data.threshold_pyg || 0)); })
      .catch(() => {});
    axios.get(`${API}/purchases/ai-auto-apply-settings`, { withCredentials: true })
      .then(({ data }) => {
        setAiSettings(data);
        setAiInput({
          enabled: !!data.enabled,
          confidence_threshold: String(data.confidence_threshold ?? 95),
          max_total_diff_pct: String(data.max_total_diff_pct ?? 5),
        });
      })
      .catch(() => {});
    if (isAdmin) {
      axios.get(`${API}/settings/users`, { withCredentials: true })
        .then(({ data }) => setUsers(data))
        .finally(() => setLoadingUsers(false));
    } else {
      setLoadingUsers(false);
    }
  }, [isAdmin]);

  const handleSaveLiquidity = async () => {
    const threshold = Number(liquidityInput);
    if (isNaN(threshold) || threshold < 0) { toast.error("Ingrese un umbral válido"); return; }
    setSavingLiquidity(true);
    try {
      await axios.put(`${API}/settings/liquidity-threshold`, { threshold_pyg: threshold }, { withCredentials: true });
      setLiquidityThreshold(threshold);
      toast.success("Umbral de liquidez actualizado");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar");
    } finally { setSavingLiquidity(false); }
  };

  const handleSaveRate = async () => {
    const rate = Number(rateInput);
    if (!rate || rate < 1000) { toast.error("Ingrese un tipo de cambio válido (mínimo 1,000)"); return; }
    setSavingRate(true);
    try {
      const { data } = await axios.put(`${API}/tipo-cambio`, { usd_pyg: rate }, { withCredentials: true });   // Fix FG
      setExchangeRate(data);
      setHistorialTC(h => [data, ...h.filter(x => x.fecha !== data.fecha)]);
      toast.success("Tipo de cambio del día corregido");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar");
    } finally { setSavingRate(false); }
  };

  const handleSaveAISettings = async () => {
    const threshold = Number(aiInput.confidence_threshold);
    const maxDiff = Number(aiInput.max_total_diff_pct);
    if (isNaN(threshold) || threshold < 0 || threshold > 100) { toast.error("Umbral debe estar entre 0 y 100"); return; }
    if (isNaN(maxDiff) || maxDiff < 0 || maxDiff > 100) { toast.error("Diferencia máxima debe estar entre 0 y 100"); return; }
    setSavingAI(true);
    try {
      const payload = { enabled: !!aiInput.enabled, confidence_threshold: threshold, max_total_diff_pct: maxDiff };
      const r = await axios.put(`${API}/purchases/ai-auto-apply-settings`, payload, { withCredentials: true });
      setAiSettings(r.data);
      toast.success("Configuración IA actualizada");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Error al actualizar");
    } finally { setSavingAI(false); }
  };

  const fetchUsers = () => {
    if (isAdmin) {
      axios.get(`${API}/settings/users`, { withCredentials: true }).then(({ data }) => setUsers(data));
    }
  };

  const handleDownloadBackup = async () => {
    // handler kept for future use
  };

  return (
    <div className="p-4 sm:p-6 lg:p-8 space-y-6">
      <div>
        <h1 className="text-2xl sm:text-3xl font-bold text-gray-900 tracking-tight">Configuración</h1>
        <p className="text-sm text-gray-500 mt-0.5">Gestión del sistema ERP CEN Ingeniería</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Exchange Rate */}
        <div className="bg-white rounded-xl border border-gray-200/60 p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2.5 bg-blue-50 rounded-xl"><DollarSign size={18} className="text-[#2E6DA4]" /></div>
            <div>
              <h3 className="font-semibold text-gray-900">Tipo de Cambio</h3>
              <p className="text-xs text-gray-500">USD/PYG del día · se toma solo (BCP) y se puede corregir</p>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Guaraníes por 1 USD (hoy)</label>
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">₲</span>
                  <input
                    type="number"
                    value={rateInput}
                    onChange={(e) => setRateInput(e.target.value)}
                    className="w-full border border-gray-300 rounded-xl pl-7 pr-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4]"
                    data-testid="exchange-rate-input"
                  />
                </div>
                <button
                  onClick={handleSaveRate}
                  disabled={savingRate}
                  className="flex items-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white px-4 py-2.5 rounded-xl text-sm font-semibold transition-all disabled:opacity-60"
                  data-testid="save-exchange-rate-btn"
                >
                  {savingRate ? <Loader2 size={14} className="animate-spin" /> : <RefreshCw size={14} />}
                  Actualizar
                </button>
              </div>
            </div>
            <div className="bg-gray-50 rounded-xl p-4 border border-gray-100 space-y-2 text-sm text-gray-600">
              <div className="flex justify-between">
                <span>1 USD =</span>
                <span className="font-bold text-gray-900">₲ {Number(exchangeRate.usd_pyg).toLocaleString("es-PY")}</span>
              </div>
              <div className="flex justify-between">
                <span>1.000 USD =</span>
                <span className="font-semibold text-[#2E6DA4]">₲ {(exchangeRate.usd_pyg * 1000).toLocaleString("es-PY")}</span>
              </div>
              <div className="flex justify-between">
                <span>IVA aplicado:</span>
                <span className="font-semibold text-amber-600">10% (DNIT)</span>
              </div>
              <div className="flex justify-between" data-testid="exchange-rate-fuente">
                <span>Fuente:</span>
                <span className="font-semibold text-gray-700">{exchangeRate.fuente || "—"}{exchangeRate.updated_by_name && exchangeRate.fuente === "manual" ? ` · ${exchangeRate.updated_by_name}` : ""}</span>
              </div>
            </div>
            {historialTC.length > 0 && (
              <div className="border border-gray-100 rounded-xl overflow-hidden" data-testid="exchange-rate-historial">
                <p className="px-3 py-2 text-[11px] font-semibold text-gray-500 uppercase bg-gray-50">Últimos días</p>
                {historialTC.slice(0, 10).map(h => (
                  <div key={h.fecha} className="flex justify-between px-3 py-1.5 text-xs border-t border-gray-100">
                    <span className="text-gray-500">{h.fecha.split("-").reverse().join("/")}</span>
                    <span className="font-semibold text-gray-800 tabular-nums">₲ {Number(h.usd_pyg).toLocaleString("es-PY")}</span>
                    <span className="text-gray-400">{h.fuente}</span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Liquidity Threshold */}
        <div className="bg-white rounded-xl border border-gray-200/60 p-6 shadow-sm">
          <div className="flex items-center gap-3 mb-5">
            <div className="p-2.5 bg-amber-50 rounded-xl"><Bell size={18} className="text-amber-600" /></div>
            <div>
              <h3 className="font-semibold text-gray-900">Umbral de Liquidez</h3>
              <p className="text-xs text-gray-500">Alerta cuando el saldo baja de este límite</p>
            </div>
          </div>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Saldo mínimo (PYG)</label>
              <div className="flex gap-3">
                <div className="relative flex-1">
                  <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">₲</span>
                  <input
                    type="number"
                    value={liquidityInput}
                    onChange={(e) => setLiquidityInput(e.target.value)}
                    placeholder="50000000"
                    className="w-full border border-gray-300 rounded-xl pl-7 pr-3 py-2.5 text-sm focus:ring-2 focus:ring-[#2E6DA4]/20 focus:border-[#2E6DA4]"
                    data-testid="liquidity-threshold-input"
                  />
                </div>
                <button
                  onClick={handleSaveLiquidity}
                  disabled={savingLiquidity || !isAdmin}
                  className="flex items-center gap-2 bg-amber-600 hover:bg-amber-700 text-white px-4 py-2.5 rounded-xl text-sm font-semibold transition-all disabled:opacity-60"
                  data-testid="save-liquidity-btn"
                >
                  {savingLiquidity ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle2 size={14} />}
                  Guardar
                </button>
              </div>
            </div>
            <div className="bg-amber-50 rounded-xl p-4 border border-amber-100 space-y-2 text-sm text-gray-600">
              <div className="flex justify-between">
                <span>Umbral actual:</span>
                <span className="font-bold text-amber-700">{formatPYGCompact(liquidityThreshold)}</span>
              </div>
              <div className="flex justify-between">
                <span>Alerta:</span>
                <span className="font-semibold text-gray-700">
                  {liquidityThreshold > 0 ? `Si saldo < ${formatPYGCompact(liquidityThreshold)}` : "Desactivada (= 0)"}
                </span>
              </div>
              <p className="text-xs text-gray-400 pt-1">Se genera una alerta en el módulo Finanzas cuando el saldo global es inferior a este umbral.</p>
            </div>
          </div>
        </div>
      </div>

      {/* IA Auto-Apply Settings */}
      <div className="bg-white rounded-xl border border-gray-200/60 p-6 shadow-sm">
        <div className="flex items-center gap-3 mb-5">
          <div className="p-2.5 bg-violet-50 rounded-xl"><Brain size={18} className="text-violet-600" /></div>
          <div>
            <h3 className="font-semibold text-gray-900">Aplicación Automática con IA</h3>
            <p className="text-xs text-gray-500">Cruce de facturas con OC: aplica automáticamente si la confianza supera el umbral</p>
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center gap-3 bg-gray-50 rounded-xl p-4 border border-gray-100">
            <input type="checkbox" checked={!!aiInput.enabled}
              onChange={(e) => setAiInput({ ...aiInput, enabled: e.target.checked })}
              disabled={!isAdmin}
              className="w-4 h-4 accent-violet-600"
              data-testid="ai-enabled-input" />
            <div>
              <p className="text-sm font-semibold text-gray-800">Habilitado</p>
              <p className="text-[11px] text-gray-500">{aiInput.enabled ? "Auto-aplicar activo" : "Solo análisis manual"}</p>
            </div>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Umbral de Confianza (%)</label>
            <input type="number" min="0" max="100" value={aiInput.confidence_threshold}
              onChange={(e) => setAiInput({ ...aiInput, confidence_threshold: e.target.value })}
              disabled={!isAdmin}
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500"
              data-testid="ai-threshold-input" />
            <p className="text-[10px] text-gray-400 mt-1">Default 95. Aplica si conf. ≥ umbral.</p>
          </div>
          <div>
            <label className="block text-xs font-semibold text-gray-700 mb-1.5">Diferencia máx de Total (%)</label>
            <input type="number" min="0" max="100" step="0.1" value={aiInput.max_total_diff_pct}
              onChange={(e) => setAiInput({ ...aiInput, max_total_diff_pct: e.target.value })}
              disabled={!isAdmin}
              className="w-full border border-gray-300 rounded-xl px-3 py-2.5 text-sm focus:ring-2 focus:ring-violet-500/20 focus:border-violet-500"
              data-testid="ai-maxdiff-input" />
            <p className="text-[10px] text-gray-400 mt-1">Si difiere más, no auto-aplica.</p>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-between">
          <p className="text-[11px] text-gray-500">
            Actual: {aiSettings.enabled ? "ACTIVO" : "INACTIVO"} · umbral {aiSettings.confidence_threshold}% · diff máx {aiSettings.max_total_diff_pct}%
          </p>
          <button onClick={handleSaveAISettings} disabled={savingAI || !isAdmin}
            className="flex items-center gap-2 bg-violet-600 hover:bg-violet-700 text-white px-4 py-2 rounded-xl text-xs font-semibold transition-all disabled:opacity-60"
            data-testid="save-ai-settings-btn">
            {savingAI ? <Loader2 size={13} className="animate-spin" /> : <CheckCircle2 size={13} />}
            Guardar
          </button>
        </div>
      </div>
      {isAdmin && (
        <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm overflow-hidden">
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-50 rounded-xl"><Users size={16} className="text-[#2E6DA4]" /></div>
              <div>
                <h3 className="font-semibold text-gray-900">Gestión de Usuarios</h3>
                <p className="text-xs text-gray-500">{users.length} usuario{users.length !== 1 ? "s" : ""} en el sistema</p>
              </div>
            </div>
            <button
              onClick={() => setShowNewUser(true)}
              className="flex items-center gap-2 bg-[#2E6DA4] hover:bg-[#245A8A] text-white px-3 py-2 rounded-xl text-xs font-semibold transition-all"
              data-testid="new-user-btn"
            >
              <Plus size={14} /> Nuevo Usuario
            </button>
          </div>
          {loadingUsers ? (
            <div className="p-5 space-y-3">{[1, 2, 3].map(i => <Skeleton key={i} className="h-12 w-full" />)}</div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50/80 border-b border-gray-100">
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Nombre</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden sm:table-cell">Email</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Rol</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Estado</th>
                    <th className="text-left px-5 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide hidden md:table-cell">Creado</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-50">
                  {users.map((u) => (
                    <tr key={u._id} className="hover:bg-gray-50/50 transition-colors" data-testid="user-row">
                      <td className="px-5 py-3">
                        <div className="flex items-center gap-2.5">
                          <div className="w-7 h-7 rounded-full bg-[#2E6DA4]/10 flex items-center justify-center flex-shrink-0">
                            <span className="text-[#2E6DA4] text-xs font-bold">{u.name?.charAt(0) || "?"}</span>
                          </div>
                          <span className="font-medium text-gray-800">{u.name}</span>
                        </div>
                      </td>
                      <td className="px-5 py-3 text-gray-500 hidden sm:table-cell text-xs">{u.email}</td>
                      <td className="px-5 py-3">
                        <span className="inline-flex items-center gap-1 text-xs bg-blue-50 text-blue-700 px-2.5 py-0.5 rounded-full font-medium">
                          <Shield size={10} />{u.role}
                        </span>
                      </td>
                      <td className="px-5 py-3">
                        <span className={`text-xs px-2.5 py-0.5 rounded-full font-semibold ${u.is_active !== false ? "bg-emerald-100 text-emerald-700" : "bg-gray-100 text-gray-500"}`}>
                          {u.is_active !== false ? "Activo" : "Inactivo"}
                        </span>
                      </td>
                      <td className="px-5 py-3 text-gray-500 text-xs hidden md:table-cell">{formatDate(u.created_at)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}

      <NewUserModal open={showNewUser} onClose={() => setShowNewUser(false)} onSaved={fetchUsers} />

      {/* Backup & Migration (Superadmin only) */}
      {isSuperAdmin && (
        <div className="bg-white rounded-xl border border-gray-200/60 shadow-sm overflow-hidden">
          <div className="flex items-center gap-3 px-5 py-4 border-b border-gray-100">
            <div className="p-2 bg-emerald-50 rounded-xl"><Archive size={16} className="text-emerald-600" /></div>
            <div>
              <h3 className="font-semibold text-gray-900">Backup Completo del Sistema</h3>
              <p className="text-xs text-gray-500">Base de datos completa + archivos adjuntos, con manifest de verificación — apto para restaurar</p>
            </div>
          </div>
          <div className="p-5 space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 text-sm text-amber-800">
              <p className="font-semibold mb-1">¿Qué incluye el backup?</p>
              <ul className="list-disc list-inside space-y-0.5 text-xs text-amber-700">
                <li>Toda la base de datos MongoDB (todas las colecciones, formato extended-JSON con ObjectId/fechas preservados)</li>
                <li>Archivos GridFS y adjuntos subidos (uploads)</li>
                <li>manifest.json con hash MD5 por colección/archivo para verificar integridad</li>
                <li>Formato compatible con la sección "Restaurar Backup" (abajo) para importarlo de vuelta</li>
              </ul>
            </div>

            {/* Link directo de descarga */}
            <div>
              <p className="text-xs font-semibold text-gray-600 mb-2 uppercase tracking-wide">Link de descarga directa</p>
              <div className="flex items-center gap-2 bg-gray-50 border border-gray-200 rounded-xl px-4 py-3">
                <LinkIcon size={14} className="text-gray-400 flex-shrink-0" />
                <span
                  className="flex-1 text-xs text-gray-600 font-mono truncate select-all"
                  data-testid="backup-url-text"
                >
                  {backupUrl}
                </span>
                <button
                  onClick={() => {
                    navigator.clipboard.writeText(backupUrl).then(() => {
                      setLinkCopied(true);
                      toast.success("Link copiado al portapapeles");
                      setTimeout(() => setLinkCopied(false), 3000);
                    });
                  }}
                  className="flex items-center gap-1.5 text-xs font-medium px-3 py-1.5 rounded-lg border border-gray-200 bg-white hover:bg-gray-50 text-gray-600 transition-all flex-shrink-0"
                  data-testid="copy-backup-link-btn"
                  title="Copiar enlace"
                >
                  {linkCopied ? <><Check size={12} className="text-emerald-500" /> Copiado</> : <><Copy size={12} /> Copiar</>}
                </button>
              </div>
              <p className="text-xs text-gray-400 mt-1.5">
                Abre este link en el navegador del deploy (mientras estés logueado) para descargar el ZIP.
              </p>
            </div>

            {/* Botón de descarga directa */}
            <a
              href={backupUrl}
              download
              className="inline-flex items-center gap-2 bg-emerald-600 hover:bg-emerald-700 text-white px-5 py-2.5 rounded-xl text-sm font-semibold transition-all shadow-sm no-underline"
              data-testid="download-backup-btn"
            >
              <Download size={15} /> Descargar ahora (ZIP)
            </a>

            <p className="text-xs text-gray-400">
              Solo accesible para Superadministrador. El ZIP incluye credenciales de usuarios (hash) —
              conservar en lugar seguro.
            </p>
          </div>
        </div>
      )}

      {isAdmin && <EmpresaSettingsCard />}
      {isAdmin && <TextosPresupuestoCard />}
      {isAdmin && <AprobadoresPedidosCard />}

      {isSuperAdmin && <RestoreBackupCard />}

      {isSuperAdmin && <TestDataCleanupCard />}
    </div>
  );
}
